import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Plus, Check, RefreshCw, Clock3, RefreshCcw } from "lucide-react";
import { Breadcrumb, GradientPanel, SearchInput, DataTable, PrimaryButton } from "../components/common";
import { settingsCards, subscriptionPlans, erpConnections, integrationProviders, companyColumns, memberColumns, apiKeyColumns, intelligenceColumns, partyColumns } from "../mock";

const Settings = () => {
  const [selected, setSelected] = useState("subscription");

  return (
    <div>
      <Breadcrumb items={[{ label: "Dashboard", path: "/a/dashboard" }, { label: "Settings" }]} />
      <GradientPanel>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage subscriptions, company setup, integrations, API access, and workspace configuration.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          {settingsCards.map((c) => {
            const Icon = Icons[c.icon] || Icons.Settings;
            const active = selected === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all ${active ? "border-blue-300 ring-1 ring-blue-200 bg-gradient-to-r from-blue-50/60 to-orange-50/40" : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"}`}
              >
                <span className="text-gray-500 mt-0.5"><Icon size={18} /></span>
                <span>
                  <span className="block text-sm font-semibold text-gray-800">{c.title}</span>
                  <span className="block text-xs text-gray-400 mt-0.5">{c.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-7">
          <SettingsContent selected={selected} />
        </div>
      </GradientPanel>
    </div>
  );
};

const SectionHead = ({ title, desc }) => (
  <div className="mb-4">
    <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    <p className="text-sm text-gray-500 mt-0.5">{desc}</p>
  </div>
);

const TableSection = ({ title, desc, columns, withNew = true }) => {
  const [q, setQ] = useState("");
  return (
    <div className="fade-up">
      <SectionHead title={title} desc={desc} />
      <div className="flex items-center justify-between gap-3">
        {withNew ? <PrimaryButton icon={Plus}>New</PrimaryButton> : <span />}
        <SearchInput value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
      </div>
      <DataTable columns={columns} rows={[]} />
    </div>
  );
};

const Subscription = () => {
  const [tab, setTab] = useState("current");
  const [planTab, setPlanTab] = useState("einvoice");
  const plans = planTab === "einvoice" ? subscriptionPlans.eInvoicePlans : subscriptionPlans.aiAgentPlans;
  return (
    <div className="fade-up">
      <SectionHead title="Manage subscription" desc="Manage plan, billing, and subscription access." />
      <div className="inline-flex gap-2 mb-5">
        {[{ id: "current", l: "Current Plan" }, { id: "usage", l: "Usage" }, { id: "invoice", l: "Invoice" }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`text-sm font-medium px-4 py-1.5 rounded-lg transition ${tab === t.id ? "pink-gradient text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "current" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {subscriptionPlans.current.map((p) => (
              <div key={p.name} className="border border-gray-100 rounded-2xl p-5 card-soft">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                  <div className="flex items-center gap-2">
                    <Clock3 size={16} className="text-emerald-500" />
                    <span className="text-xs font-semibold text-emerald-600">{p.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                  <div><p className="text-gray-400 text-xs">Annual Fee</p><p className="font-bold text-gray-900 text-lg"><span className="text-xs align-top">$</span>{p.fee}</p></div>
                  <div><p className="text-gray-400 text-xs">Current Plan</p><p className="font-semibold text-blue-600">{p.planLabel}</p></div>
                  <div><p className="text-gray-400 text-xs">Expires</p><p className="font-medium text-gray-700">{p.expires}</p></div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-400 mb-1"><span>Credit usage</span><span>{p.creditUsed} / {p.creditTotal} used</span></div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500" style={{ width: "2%" }} /></div>
                </div>
                <p className="text-xs text-gray-500 mt-3 leading-relaxed">{p.note}</p>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-bold text-gray-900 mt-7">Available Plans</h3>
          <p className="text-sm text-gray-500">Choose the plan that best fits your needs. You can upgrade, downgrade, or cancel anytime. For custom plans or enterprise solutions, please contact our sales team.</p>
          <div className="inline-flex gap-2 my-4">
            <button onClick={() => setPlanTab("einvoice")} className={`text-sm font-medium px-4 py-1.5 rounded-lg transition ${planTab === "einvoice" ? "pink-gradient text-white" : "border border-gray-200 text-gray-600"}`}>E-Invoice</button>
            <button onClick={() => setPlanTab("aiagent")} className={`text-sm font-medium px-4 py-1.5 rounded-lg transition ${planTab === "aiagent" ? "pink-gradient text-white" : "border border-gray-200 text-gray-600"}`}>AI Agent</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {plans.map((pl) => (
              <div key={pl.name} className={`relative rounded-2xl border p-4 ${pl.popular ? "border-rose-300 ring-1 ring-rose-200" : "border-gray-100"} card-soft`}>
                {pl.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-white pink-gradient px-2 py-0.5 rounded-full">POPULAR</span>}
                <p className="text-center text-xs font-bold text-gray-500 tracking-wide">{pl.name}</p>
                <p className="text-center mt-2"><span className="text-xs align-top">$</span><span className="text-2xl font-bold text-gray-900">{pl.price}</span><span className="text-[11px] text-gray-400"> (Per Year)</span></p>
                <p className="text-center text-xs text-gray-500 mt-1">{pl.docs}</p>
                <div className="mt-3">
                  {pl.active ? (
                    <div className="text-center text-xs font-semibold text-emerald-600 bg-emerald-50 py-1.5 rounded-lg">Active</div>
                  ) : (
                    <button className="w-full text-sm font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 py-1.5 rounded-lg transition">Upgrade</button>
                  )}
                </div>
                <ul className="mt-3 space-y-1.5">
                  {subscriptionPlans.features.slice(0, 3).map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-[11px] text-gray-500"><Check size={12} className="text-emerald-500 mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "usage" && <DataTable columns={["Service", "Used", "Limit", "Period"]} rows={[]} />}
      {tab === "invoice" && <DataTable columns={["Invoice No", "Amount", "Status", "Date"]} rows={[]} />}
    </div>
  );
};

const ErpList = () => (
  <div className="fade-up">
    <SectionHead title="ERP/CRM Connections" desc="Set up ERP and CRM system connections." />
    <div className="space-y-1">
      {erpConnections.map((e) => (
        <div key={e.id} className="flex items-center gap-4 py-3.5 border-b border-gray-50 last:border-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: e.color || "#1d4ed8" }}>{e.letter || "AI"}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800">{e.name}</p>
            <p className="text-xs text-gray-400 line-clamp-2">{e.desc}</p>
          </div>
          <button className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 border border-emerald-200 hover:bg-emerald-50 px-3.5 py-1.5 rounded-lg transition shrink-0"><Icons.Cable size={15} /> Connect</button>
        </div>
      ))}
    </div>
  </div>
);

const IntegrationGrid = () => (
  <div className="fade-up">
    <SectionHead title="Integration & Setup" desc="Connect third-party tools and integrations." />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {integrationProviders.map((p) => (
        <div key={p.id} className="flex items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ background: p.color }}>{p.letter}</div>
          <span className="text-sm text-gray-700 flex-1 truncate">{p.name}</span>
          <button className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 px-2.5 py-1 rounded-lg transition"><RefreshCcw size={12} /> Connect</button>
        </div>
      ))}
    </div>
  </div>
);

const ApiKey = () => (
  <div className="fade-up">
    <SectionHead title="API Key" desc="Create and manage secure API access keys." />
    <div className="flex items-center justify-between flex-wrap gap-4">
      <div className="flex items-center gap-2 text-gray-800 font-medium text-sm"><Icons.KeyRound size={16} /> API Key Management</div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Name</label>
        <input defaultValue="ApiKey" className="px-3 py-2 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-blue-200" />
      </div>
      <span className="text-[11px] font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded self-start mt-5">DISABLED</span>
    </div>
    <button className="w-full mt-4 inline-flex items-center justify-center gap-2 text-sm font-semibold text-white pink-gradient py-2.5 rounded-lg hover:opacity-95 transition"><RefreshCw size={15} /> Regenerate API Key</button>
    <DataTable columns={apiKeyColumns} rows={[]} />
  </div>
);

const SettingsContent = ({ selected }) => {
  switch (selected) {
    case "subscription": return <Subscription />;
    case "company": return <TableSection title="Company Settings" desc="Configure company setup and master settings." columns={companyColumns} />;
    case "party": return <TableSection title="Party Companies" desc="Manage party and company related records." columns={partyColumns} />;
    case "erp": return <ErpList />;
    case "member": return <TableSection title="Member" desc="Manage users, roles, and workspace members." columns={memberColumns} withNew={false} />;
    case "apikey": return <ApiKey />;
    case "integration": return <IntegrationGrid />;
    case "intelligence": return <TableSection title="Intelligence hub" desc="Configure intelligence hub and data pipelines." columns={intelligenceColumns} />;
    default: return <Subscription />;
  }
};

export default Settings;
